import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchCodigoOrdenConfigs,
    CodigoOrdenConfig
} from '../../../services/codigoOrdenService';
import AddOrdenPage from '../../../components/Orden/AddOrdenPage';
import EditOrdenPage from '../../../components/Orden/EditOrdenPage';
import DeleteOrdenPage from '../../../components/Orden/DeleteOrdenPage';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const CodigoOrdenPage: React.FC = () => {
    const [configs, setConfigs] = useState<CodigoOrdenConfig[]>([]);
    const [showAddModal, setShowAddModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [selectedConfig, setSelectedConfig] = useState<CodigoOrdenConfig | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteConfig, setDeleteConfig] = useState<CodigoOrdenConfig | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const itemsPerPage = 10;

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('codigoordenconfig', userLevelId);

    const loadConfigs = useCallback(async () => {
        setLoading(true);
        try {
            if (perm?.can_read) {
                const data = await fetchCodigoOrdenConfigs();
                const sortedData = [...data].sort((a, b) => a.id - b.id);
                setConfigs(sortedData);
            } else {
                setConfigs([]);
            }
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm]);

    useEffect(() => {
        if (perm !== null) {
            loadConfigs();
        }
    }, [loadConfigs, perm]);

    const totalPages = Math.ceil(configs.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedConfigs = configs.slice(startIndex, startIndex + itemsPerPage);

    const handleCreate = () => setShowAddModal(true);

    const handleConfigCreated = (newConfig: CodigoOrdenConfig) => {
        setConfigs(prev => {
            const updatedConfigs = prev.map(cfg =>
                newConfig.activo ? { ...cfg, activo: false } : cfg
            );
            return [...updatedConfigs, newConfig].sort((a, b) => a.id - b.id);
        });
    };

    const handleConfigUpdated = (updatedConfig: CodigoOrdenConfig) => {
        setConfigs(prev => {
            // Desactivar todos los demás si el editado está activo
            return prev.map(cfg => {
                if (cfg.id === updatedConfig.id) {
                    return updatedConfig;
                }
                return updatedConfig.activo ? { ...cfg, activo: false } : cfg;
            });
        });
    };

    const handleEdit = (cfg: CodigoOrdenConfig) => {
        setSelectedConfig(cfg);
        setShowEditModal(true);
    };

    const handleConfigDeleted = (deletedId: number) => {
        setConfigs(prev => prev.filter(cfg => cfg.id !== deletedId));
        setShowDeleteModal(false);
        setDeleteConfig(null);
    };
    const handleDeleteClick = (cfg: CodigoOrdenConfig) => {
        setDeleteConfig(cfg);
        setShowDeleteModal(true);
    };

    if (perm === null) return <Loading text="Cargando códigos de orden..." />;

    return <><LumaResourceWorkspace title="Código de orden" description="Consulta y gestiona la información de tu coworking." group="Configuraciones" rows={configs} columns={[{key:'nombre',label:'Nombre'},{key:'prefijo',label:'Prefijo'},{key:'activo',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setShowAddModal(true)}:undefined} onEdit={perm?.can_update?handleEdit:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined} history=""/><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />{showAddModal && (<AddOrdenPage
                        onClose={() => setShowAddModal(false)}
                        onCreated={handleConfigCreated}
                    />)}{showEditModal && selectedConfig && (<EditOrdenPage
                        config={selectedConfig}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedConfig(null);
                        }}
                        onUpdated={handleConfigUpdated}
                    />)}{showDeleteModal && deleteConfig && (<DeleteOrdenPage
                        id={deleteConfig.id}
                        prefijo={deleteConfig?.prefijo ?? ''}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setDeleteConfig(null);
                        }}
                        onDeleted={handleConfigDeleted}
                    />)}</>;
};

export default CodigoOrdenPage;
